import { useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { apiUrls, httpClient as axiosInstance } from "@/src/api"
import { useWorkspaceTogglesStore } from "@/src/store"
import { Permission } from "@/src/auth/permissions"
import { useHasPermission } from "@/src/hooks/usePermissions"

import type { ApiResponse } from "@/src/types/apiResponse"

export interface WorkspaceTogglesAddons {
    cefr: boolean
    pulse: number
    proctoring: boolean
    maxCreditsTBDPerRound: number
}

export interface CreditSettings {
    isEnabled: boolean
    totalCredits: number
    addons: WorkspaceTogglesAddons
}

export interface WorkspaceToggles {
    workspaceId?: string
    workspaceName?: string
    /** Outbound audio enabled at workspace level. */
    outboundAudioEnabled?: boolean
    /** Enables interview scheduling task creation for this workspace. */
    interviewSchedulingEnabled?: boolean
    credits?: {
        videoCredits: CreditSettings
        audioCredits: CreditSettings
        /** Outbound audio credits config (no outbound video). */
        outboundAudioCredits?: CreditSettings
        chargeAttemptRule: string
        maxAttempts: number
    }
}

/** Raw GET /workspace-toggles body (may include extra root fields not stored). */
export type WorkspaceTogglesApiResponse = WorkspaceToggles & {
    outboundAudioEnabled?: boolean
    interviewSchedulingEnabled?: boolean
}

const defaultAddons = (): WorkspaceTogglesAddons => ({
    cefr: false,
    pulse: 0,
    proctoring: false,
    maxCreditsTBDPerRound: 0,
})

/**
 * Single place that maps the API JSON to what we persist (`WorkspaceToggles`).
 * Enabled flags for each credit bucket live under `credits.*.isEnabled` only.
 */
export function parseWorkspaceTogglesFromApi(raw: WorkspaceTogglesApiResponse | null | undefined): WorkspaceToggles {
    if (raw == null || typeof raw !== "object") {
        return {}
    }

    const {
        outboundAudioEnabled: rootOutboundFlag,
        interviewSchedulingEnabled,
        workspaceId,
        workspaceName,
        credits: creditsFromApi,
    } = raw

    if (!creditsFromApi) {
        return { workspaceId, workspaceName, interviewSchedulingEnabled }
    }

    const credits = { ...creditsFromApi }

    if (credits.outboundAudioCredits === undefined && typeof rootOutboundFlag === "boolean") {
        credits.outboundAudioCredits = {
            isEnabled: rootOutboundFlag,
            totalCredits: credits.audioCredits?.totalCredits ?? 0,
            addons: credits.audioCredits?.addons ? { ...credits.audioCredits.addons } : defaultAddons(),
        }
    }

    return {
        workspaceId,
        workspaceName,
        interviewSchedulingEnabled,
        credits,
    }
}

export interface CreditsEnabledFlags {
    video: boolean
    audio: boolean
    outboundAudio: boolean
}

/** Reads `isEnabled` from all credit buckets in one call. */
export function getCreditsEnabled(workspaceToggles: WorkspaceToggles | null | undefined): CreditsEnabledFlags {
    const credits = workspaceToggles?.credits
    return {
        video: credits?.videoCredits?.isEnabled === true,
        audio: credits?.audioCredits?.isEnabled === true,
        outboundAudio: credits?.outboundAudioCredits?.isEnabled === true,
    }
}

export interface CefrBucketFlags {
    /** `videoCredits.addons.cefr` AND `videoCredits.isEnabled`. */
    video: boolean
    /** `audioCredits.addons.cefr` AND `audioCredits.isEnabled`. */
    audio: boolean
    /** `outboundAudioCredits.addons.cefr` AND `outboundAudioCredits.isEnabled`. */
    outboundAudio: boolean
}

export interface AddonsEnabledFlags {
    /** Proctoring is video-only. True only when video credits AND `addons.proctoring` are on. */
    proctoring: boolean
    /**
     * CEFR is intentionally per-bucket: a workspace can enable CEFR on outbound audio while
     * leaving it off on video. Treating CEFR as a single workspace-wide boolean caused
     * cross-bucket leaks (e.g. video agent showing CEFR because outbound audio had it on).
     */
    cefr: CefrBucketFlags
}

/**
 * Workspace-level add-on capability flags, derived from the monetization API.
 * These flags decide which add-ons are *allowed* to be shown / sent.
 *
 * Each add-on bucket is gated by its owning credit bucket's `isEnabled`, so a disabled
 * bucket can never leak its add-on flags into other buckets.
 */
export function getAddonsEnabled(workspaceToggles: WorkspaceToggles | null | undefined): AddonsEnabledFlags {
    const credits = workspaceToggles?.credits
    const { video, audio, outboundAudio } = getCreditsEnabled(workspaceToggles)

    const videoAddonSrc = video ? credits?.videoCredits?.addons : undefined
    const audioAddonSrc = audio ? credits?.audioCredits?.addons : undefined
    const outboundAudioAddonSrc = outboundAudio ? credits?.outboundAudioCredits?.addons : undefined

    return {
        proctoring: videoAddonSrc?.proctoring === true,
        cefr: {
            video: videoAddonSrc?.cefr === true,
            audio: audioAddonSrc?.cefr === true,
            outboundAudio: outboundAudioAddonSrc?.cefr === true,
        },
    }
}

export type EnabledFlag = "ENABLED" | "DISABLED"

export interface ClampedRoundPlanAddOn {
    proctoringEnabled: EnabledFlag
    storeInterviewRecording: EnabledFlag
    advancedEvaluationView: EnabledFlag
    outreachEnabled: EnabledFlag
    outreachEnableType: "PRO" | "ADVANCED"
    interviewReminderEnabled: EnabledFlag
    interviewReminderType: "PRO" | "ADVANCED"
    directionalCefr: EnabledFlag
    intentChecker: EnabledFlag
}

export interface ClampRoundPlanAddOnOptions {
    isVideo: boolean
    aiEnabled?: boolean
    /**
     * Round directions selected by the user. Required to clamp CEFR correctly for audio
     * agents — CEFR for an audio round must be charged against the audio bucket if INBOUND
     * is selected and against the outbound bucket if OUTBOUND is selected.
     *
     * If neither flag is provided, CEFR for audio rounds defaults to disallowed
     * (restrictive — better to under-enable on the wire than over-enable).
     */
    isInboundEnabled?: boolean
    isOutboundEnabled?: boolean
    workspaceToggles: WorkspaceToggles | null | undefined
}

const toRoundPlanAddOnBoolean = (value: unknown): boolean => {
    if (typeof value === "boolean") return value
    if (typeof value === "string") return value === "ENABLED"
    return false
}

/**
 * Single source of truth for serializing `roundPlanAddOn` to the API shape.
 *
 * Rule: monetization API decides what is *allowed*; the form value is what the user
 * *selected*. Final payload = `apiAllows && userSelected`. If either is false, send DISABLED.
 *
 * - `proctoringEnabled` requires the proctoring workspace add-on AND a video agent AND user toggle on.
 * - `storeInterviewRecording` requires a video agent AND user toggle on.
 * - `directionalCefr` requires the CEFR workspace add-on AND user toggle on.
 * - `interviewReminderEnabled` is force-disabled when the round is AI-led.
 */
export function clampRoundPlanAddOnForPayload(
    formAddOn: any,
    options: ClampRoundPlanAddOnOptions
): ClampedRoundPlanAddOn {
    const apiCredits = getCreditsEnabled(options.workspaceToggles)
    const apiAddons = getAddonsEnabled(options.workspaceToggles)

    const userProctoring = toRoundPlanAddOnBoolean(formAddOn?.proctoringEnabled)
    const userStoreRecording = toRoundPlanAddOnBoolean(formAddOn?.storeInterviewRecording)
    const userAdvancedEvaluation = toRoundPlanAddOnBoolean(formAddOn?.advancedEvaluationView)
    const userOutreach = toRoundPlanAddOnBoolean(formAddOn?.outreachEnabled)
    const userInterviewReminder = toRoundPlanAddOnBoolean(formAddOn?.interviewReminderEnabled)
    const userDirectionalCefr = toRoundPlanAddOnBoolean(formAddOn?.directionalCefr)
    const userIntentChecker = toRoundPlanAddOnBoolean(formAddOn?.intentChecker)

    // Video-only add-ons additionally require video credits to be enabled in the
    // monetization API, so a stale agentType cannot leak proctoring/recording on.
    const proctoringAllowed = apiCredits.video && apiAddons.proctoring && options.isVideo
    const storeRecordingAllowed = apiCredits.video && options.isVideo
    // CEFR is per-bucket. For a video round it must come from the video bucket. For an
    // audio round it must be enabled in EVERY selected direction's owning bucket
    // (INBOUND→audio, OUTBOUND→outboundAudio). This mirrors the strict UI rule: if any
    // selected direction's bucket lacks CEFR, the toggle isn't shown — and even if form
    // state drifted, the wire refuses to charge a partial CEFR run.
    const inboundSelected = !!options.isInboundEnabled
    const outboundSelected = !!options.isOutboundEnabled
    const audioCefrAllowedForSelection =
        (inboundSelected || outboundSelected) &&
        (!inboundSelected || apiAddons.cefr.audio) &&
        (!outboundSelected || apiAddons.cefr.outboundAudio)
    const cefrAllowed = options.isVideo ? apiAddons.cefr.video : audioCefrAllowedForSelection

    return {
        proctoringEnabled: proctoringAllowed && userProctoring ? "ENABLED" : "DISABLED",
        storeInterviewRecording: storeRecordingAllowed && userStoreRecording ? "ENABLED" : "DISABLED",
        advancedEvaluationView: userAdvancedEvaluation ? "ENABLED" : "DISABLED",
        outreachEnabled: userOutreach ? "ENABLED" : "DISABLED",
        outreachEnableType: formAddOn?.outreachEnableType || "PRO",
        interviewReminderEnabled: options.aiEnabled
            ? "DISABLED"
            : userInterviewReminder
              ? "ENABLED"
              : "DISABLED",
        interviewReminderType: formAddOn?.interviewReminderType || "PRO",
        directionalCefr: cefrAllowed && userDirectionalCefr ? "ENABLED" : "DISABLED",
        intentChecker: userIntentChecker ? "ENABLED" : "DISABLED",
    }
}

/**
 * Convenience wrapper for non-React callsites that want to read workspace
 * toggles straight from the Zustand store.
 */
export function clampRoundPlanAddOnFromStore(
    formAddOn: any,
    options: Omit<ClampRoundPlanAddOnOptions, "workspaceToggles">
): ClampedRoundPlanAddOn {
    const workspaceToggles = useWorkspaceTogglesStore.getState().workspaceToggles
    return clampRoundPlanAddOnForPayload(formAddOn, { ...options, workspaceToggles })
}

/** True when the agentType corresponds to a video interview agent. */
export function isVideoAgentType(agentType: string | null | undefined): boolean {
    return agentType === "INTERVIEW_AGENT_VIDEO" || agentType === "SCREENING_AGENT_VIDEO"
}

/**
 * Inbound = in-app video and/or audio. Outbound = outbound audio campaigns only.
 * Video never applies to outbound; audio can be inbound or outbound depending on toggles.
 */
export function getCallDirectionAvailability(
    workspaceToggles: WorkspaceToggles | null | undefined
): { showInbound: boolean; showOutbound: boolean } {
    const { video, audio, outboundAudio } = getCreditsEnabled(workspaceToggles)
    return {
        showInbound: video || audio,
        showOutbound: outboundAudio,
    }
}

export function normalizeRoundDirectionsForPayload(
    round: { isInboundEnabled?: boolean; isOutboundEnabled?: boolean },
    workspaceToggles: WorkspaceToggles | null | undefined
): { isInboundEnabled: boolean; isOutboundEnabled: boolean } {
    const { showInbound, showOutbound } = getCallDirectionAvailability(workspaceToggles)
    if (showInbound && !showOutbound) {
        return { isInboundEnabled: true, isOutboundEnabled: false }
    }
    if (!showInbound && showOutbound) {
        return { isInboundEnabled: false, isOutboundEnabled: true }
    }
    if (showInbound && showOutbound) {
        return {
            isInboundEnabled: round.isInboundEnabled ?? true,
            isOutboundEnabled: round.isOutboundEnabled ?? false,
        }
    }
    return { isInboundEnabled: true, isOutboundEnabled: false }
}

export type WorkspaceTogglesSource = "monetization" | "features"

export const getWorkspaceToggles = async (
    togglesSource: WorkspaceTogglesSource = "monetization"
): Promise<ApiResponse<WorkspaceToggles>> => {
    try {
        const requestUrl =
            togglesSource === "features"
                ? apiUrls.WORKSPACE_MONETIZATION_FEATURES()
                : apiUrls.WORKSPACE_MONETIZATION()
        const resp = await axiosInstance.get(requestUrl)
        const raw = resp?.data?.data as WorkspaceTogglesApiResponse | undefined
        return { status: "success", data: parseWorkspaceTogglesFromApi(raw) }
    } catch (e: any) {
        console.log(e)
        return { status: "error", errorMessage: e.status ?? e.message }
    }
}

function resolveWorkspaceTogglesSource(
    canViewBilling: boolean,
    canViewAgent: boolean
): WorkspaceTogglesSource | null {
    if (canViewBilling) return "monetization"
    if (canViewAgent) return "features"
    return null
}

export function useWorkspaceToggles(enabled: boolean = true) {
    const { setWorkspaceToggles, setLoading, setError, clearWorkspaceToggles } = useWorkspaceTogglesStore()
    const canViewBilling = useHasPermission(Permission.viewBilling)
    const canViewAgent = useHasPermission(Permission.viewAgent)
    const togglesSource = resolveWorkspaceTogglesSource(canViewBilling, canViewAgent)
    const shouldFetch = enabled && togglesSource != null

    useEffect(() => {
        if (!shouldFetch) {
            clearWorkspaceToggles()
        }
    }, [shouldFetch, clearWorkspaceToggles])

    return useQuery({
        queryKey: ["workspace-toggles", togglesSource],
        enabled: shouldFetch,
        staleTime: 0,
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        queryFn: async () => {
            setLoading(true)
            try {
                const resp = await getWorkspaceToggles(togglesSource!)
                if (resp.status !== "success") {
                    const errorMessage = resp.errorMessage ?? "Failed to fetch workspace toggles"
                    setError(errorMessage)
                    throw new Error(errorMessage)
                }
                const data = resp.data ?? {}
                setWorkspaceToggles(data)
                return data
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "Failed to fetch workspace toggles"
                setError(errorMessage)
                throw error
            }
        },
    })
}
